import React, { useMemo } from 'react';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import {
  CommunicationContextType,
  ComposeContextValueKind
} from '../../../lib/types/communication.types';
import { buildSessionOptionLabel, isValueKind } from './context-selector.helpers';
import { getMessages } from './context-selector.messages';
import { IContextOption, IPreloadedContextRowProps } from './context-selector.types';
import { ContextRow } from './context-row.component';
import { MisfitHelper } from './context-selector.styles';

// Feeds the generic row from the selector-fetched FULL lists (age groups,
// sessions, levels, teams): per-kind options, downward scoping, stale flags.
export const PreloadedContextRow: React.FC<IPreloadedContextRowProps> = ({
  type,
  selections,
  contexts,
  readOnly,
  ageGroups,
  sessions,
  levels,
  teams,
  optionsLoading,
  valuesLoading,
  optionsError,
  valuesError,
  onSelectionsChange,
  onTypeChange,
  onEdit,
  onApprove,
  onCancel,
  onRemove
}) => {
  const termLookup = useCompanySportTerminology();
  const messages = getMessages(termLookup);

  // Downward propagation: selected age groups scope the session options.
  // Upstream never scopes — a session selection must not narrow age groups.
  const selectedAgeGroupIds = useMemo(
    () => contexts
      .filter((row) => row.type === CommunicationContextType.AGE_GROUP && row.id)
      .map((row) => row.id as string),
    [contexts]
  );

  const options: IContextOption[] = useMemo(() => {
    switch (type) {
      case CommunicationContextType.AGE_GROUP:
        return ageGroups.map((group) => ({ key: group._id, label: group.name }));
      case CommunicationContextType.ASSESSMENT_SESSION: {
        const scoped = selectedAgeGroupIds.length
          ? sessions.filter(
            (session) => session.ageGroup && selectedAgeGroupIds.includes(session.ageGroup._id)
          )
          : sessions;
        return scoped.map((session) => ({ key: session._id, label: buildSessionOptionLabel(session) }));
      }
      case ComposeContextValueKind.LEVEL:
        return levels.map((value) => ({ key: value, label: value }));
      case ComposeContextValueKind.TEAM:
        return teams.map((value) => ({ key: value, label: value }));
      default:
        return [];
    }
  }, [type, ageGroups, sessions, levels, teams, selectedAgeGroupIds]);

  const loading = isValueKind(type) ? valuesLoading : optionsLoading;
  const hasError = isValueKind(type) ? valuesError : optionsError;

  // Stale = a selection absent from its fully-loaded FULL list (deleted
  // instance). A failed query returns [] (indeterminate) — never flag on it.
  const staleKeys = useMemo(() => {
    if (loading || hasError) return new Set<string>();
    const fullList = new Set(
      type === CommunicationContextType.AGE_GROUP ? ageGroups.map((group) => group._id)
        : type === CommunicationContextType.ASSESSMENT_SESSION ? sessions.map((session) => session._id)
          : type === ComposeContextValueKind.LEVEL ? levels : teams
    );
    const selectedKeys = selections.map(
      (row) => (isValueKind(row.type) ? row.value : row.id) as string
    );
    return new Set(selectedKeys.filter((key) => !fullList.has(key)));
  }, [loading, hasError, type, ageGroups, sessions, levels, teams, selections]);

  const helper = staleKeys.size > 0
    ? <MisfitHelper><IntlMsg {...messages.noLongerAvailable} /></MisfitHelper>
    : undefined;

  return (
    <ContextRow
      type={type}
      selections={selections}
      contexts={contexts}
      readOnly={readOnly}
      options={options}
      loading={loading}
      flaggedKeys={staleKeys}
      flagTitle="No longer available"
      helper={helper}
      onSelectionsChange={onSelectionsChange}
      onTypeChange={onTypeChange}
      onEdit={onEdit}
      onApprove={onApprove}
      onCancel={onCancel}
      onRemove={onRemove}
    />
  );
};
